/**
  Copyright (c) 2015, 2018, Oracle and/or its affiliates.
  The Universal Permissive License (UPL), Version 1.0
*/

'use strict';

define('vb/components/mobile-navigation-bar-drawer/viewModel',['jquery',
  'hammerjs',
  'ojs/ojoffcanvas',
  'ojs/ojarraydataprovider',
  'ojs/ojbutton',
  'ojs/ojnavigationlist',
], ($, Hammer, OffcanvasUtils, ArrayDataProvider) => {
  // Drawer identifiers
  const drawerParams = {
    displayMode: 'push',
    selector: '#oj-ext-mobile-app-drawer',
    content: 'div.oj-hybrid-applayout-page',
  };

  // Hammer swipe options
  const hOptions = {
    recognizers: [
      [Hammer.Swipe, { direction: Hammer.DIRECTION_HORIZONTAL }],
    ],
  };

  class NavigationBarDrawerComponentModel {
    constructor(context) {
      this.composite = context.element;

      // remember properties in an instance variable for later use
      this._props = context.properties;

      this.configure();
    }

    /**
     * Called when the composite is connected to the DOM.
     */
    connected() {
      const props = this._props;
      const dir = document.documentElement.getAttribute('dir') || '';
      const isRTL = (dir.toLowerCase() === 'rtl');

      // setup 'ojopen' and 'ojclose' listener so that we can keep our state in sync
      $(drawerParams.selector).on('ojopen', () => {
        if (!props.expanded) {
          props.expanded = true;
        }
      }).on('ojclose', () => {
        if (props.expanded) {
          props.expanded = false;
        }
      });

      // setup swipe to reveal on the main content using Hammer.js
      const contentElem = document.querySelector(drawerParams.content);
      const mc = new Hammer.Manager(contentElem, hOptions);

      mc.on('swiperight', (event) => {
        event.preventDefault();
        if (!isRTL) {
          OffcanvasUtils.open(drawerParams);
        }
      });

      mc.on('swipeleft', (event) => {
        event.preventDefault();
        if (isRTL) {
          OffcanvasUtils.open(drawerParams);
        }
      });

      // hide the navigator on swipe back of the navigator content
      mc.on('swipeleft', (event) => {
        event.preventDefault();
        if (!isRTL) {
          OffcanvasUtils.close(drawerParams);
        }
      });

      mc.on('swiperight', (event) => {
        event.preventDefault();
        if (isRTL) {
          OffcanvasUtils.close(drawerParams);
        }
      });

      // hammer messes with user selection. On mobile devices selection is a tap gesture not swipe so re-enable
      // user selection. Otherwise users cannot select any text in the content, which will be bad!
      contentElem.style.cssText += '-webkit-user-select: auto';

      // respect the initial state passed in
      return (props.expanded) ? OffcanvasUtils.open(drawerParams) : OffcanvasUtils.close(drawerParams);
    }

    /**
     * Called when a property on the composite is updated.
     * @param {Composite.PropertyChangedContext} context
     */
    propertyChanged(context) { // eslint-disable-line class-methods-use-this
      if (context.property === 'expanded') {
        if (context.value) {
          OffcanvasUtils.open(drawerParams);
        } else {
          OffcanvasUtils.close(drawerParams);
        }
      }
    }

    /**
     * Configures the composite component
     */
    configure() {
      /**
       * New, dynamic NavBar code
       *
       *  This code fetches the oj-ext-mobile-navigation-bar-drawer node
       *  and then scans that tag for any sub-   -menuitem  elements.
       *  For each of those menu items, it creates an array entry object and adds it to the navBar array.
       */

      const parseNavigationItemFromHTML = (node) => {
        const item = {};
        item.name = node.getAttribute('name');
        item.view = node.getAttribute('view');
        item.action = node.getAttribute('action');
        item.takeFocus = !(node.getAttribute('takeFocus') === 'false');
        item.iconClass = node.getAttribute('iconclass');

        return item;
      };

      const navData = [];
      const menuItems = this.composite.getElementsByTagName('oj-ext-mobile-navigation-item');

      // menuItems is not an array but htmlElement Collection
      for (let i = 0; i < menuItems.length; i += 1) {
        navData.push(parseNavigationItemFromHTML(menuItems[i]));
      }

      // This creates the oj-navigation-list compatible data source
      this.navData = navData;
      this.navDataSource = new ArrayDataProvider(navData, { idAttribute: 'action' });
    }

    /**
     * Private method to raise navigation action
     * @param sourceEvent {Event}
     * @param navItem {HTMLElement}
     * @private
     */
    _raiseNavigationAction(sourceEvent, navItem) {
      const eventParams = {
        detail: {
          value: navItem.action,
        },
      };
      this.composite.dispatchEvent(new CustomEvent('navigationAction', eventParams));
    }

    /**
     * Called by navigation drawer toggle button on any flow page
     */
    toggle() {
      this._props.expanded = !this._props.expanded;
    }

    /**
     *   This defines the CCA reaction to the selection of a navBar item.  It should check:
     *   1) The action attribute and if defined raise a shellAction with the selected navItem's
     *   configured value.
     *   2) The view attribute and if defined alter the selection property to match.
     */
    navBarChangeHandler(event) {
      // ignore selection triggered through external property changes. i.e only handle direct user interaction
      if (event.detail.updatedFrom !== 'external') {
        const navItem = this._findNavItem(event.detail.value);

        if (navItem.action) {
          this._raiseNavigationAction(event, navItem);
        }
      }
    }

    /**
     * Hook into the ojBeforeSelect event to control whether the nav list item retains highlighting,
     * based on the takeFocus attribute.
     */
    beforeSelectHandler(event) {
      // in the beforeSelect event,event.detail.key holds the selected id
      const navItem = this._findNavItem(event.detail.key);

      if (!navItem.takeFocus) {
        event.preventDefault();
      }
    }

    /**
     * returns the navigation item in the internal list that matches the target action
     * @param targetAction
     * @private
     */
    _findNavItem(targetAction) {
      return this.navData.find((navItem) => navItem.action === targetAction);
    }
  }

  return NavigationBarDrawerComponentModel;
});

